import type { AdminPermission } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnyPermission } from './usePermissions';

/**
 * Conditional-render gate. Renders `children` only when the admin holds
 * the required permission(s); otherwise renders `fallback` (a 403-ish
 * panel by default).
 *
 * - `perm` — single permission, OR
 * - `anyOf` — admin needs at least one of these.
 *
 * Wrapped in `observer` so a login / permission refresh re-evaluates the
 * gate reactively (the underlying store value is a MobX observable).
 *
 * Fail-closed: if neither prop resolves to a held permission, the gate
 * hides children. Used both for whole-page gating (with the default
 * fallback) and inline action gating (with `fallback={null}` to simply
 * hide a button).
 */
interface RequirePermissionProps {
  perm?: AdminPermission;
  anyOf?: readonly AdminPermission[];
  children: ReactNode;
  /** Rendered when the admin lacks the permission. Defaults to a 403 panel. */
  fallback?: ReactNode;
}

const DefaultForbidden = (): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      role="alert"
      sx={{
        p: 4,
        textAlign: 'center',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t('permissions.forbiddenTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('permissions.forbiddenBody')}
      </Typography>
    </Box>
  );
};

const RequirePermissionInner = ({
  perm,
  anyOf,
  children,
  fallback,
}: RequirePermissionProps): ReactElement => {
  const required = anyOf ?? (perm ? [perm] : []);
  const allowed = useAnyPermission(required);
  if (allowed) return <>{children}</>;
  return <>{fallback ?? <DefaultForbidden />}</>;
};

export const RequirePermission = observer(RequirePermissionInner);
